import type { RequestField, RequestSubmission } from '@vemedia/request-contract'

export interface BookingFormValues {
    fullname: string
    email: string
    phone: string
    message: string
    consent: boolean
}

export interface BookingSummary {
    kind?: 'tour-booking' | 'package-booking'
    itemUuid?: string
    tour: string
    guests: number
    guestsText: string
    guestBreakdown?: Record<string, number>
    travelDate?: string
    transferText?: string
    extrasText?: string
    total: number
    pageUrl: string
}

export interface TransferRequestInput {
    routeId: number
    fromPlaceId: number
    toPlaceId: number
    fromPlaceName: string
    toPlaceName: string
    tripType: 'one_way' | 'round_trip'
    pickupDate: string
    pickupTime: string
    returnDate: string
    returnTime: string
    passengers: number
    displayedTotal: number | null
    pageUrl: string
    name: string
    email: string
    phone: string
    website?: string
}

export const EMPTY_BOOKING_FORM: BookingFormValues = {
    fullname: '',
    email: '',
    phone: '',
    message: '',
    consent: false,
}

export function requiredText(value: string, message: string) {
    return value.trim() ? undefined : message
}

export function validEmail(value: string) {
    const email = value.trim()
    if (!email)
        return 'Enter your email address.'

    const at = email.indexOf('@')
    const dot = email.lastIndexOf('.')
    const valid = at > 0
        && at === email.lastIndexOf('@')
        && dot > at + 1
        && dot < email.length - 1
        && ![...email].some(character => /\s/.test(character))
    return valid ? undefined : 'Enter a valid email address.'
}

export function buildBookingRequest(
    values: BookingFormValues,
    booking: BookingSummary,
    website = '',
): RequestSubmission {
    const fields: RequestField[] = [
        { key: 'guests', label: 'Guests', value: booking.guestsText },
        { key: 'travelDate', label: 'Travel date', value: booking.travelDate || null, format: 'date' },
        { key: 'transfer', label: 'Transfer', value: booking.transferText || null },
        { key: 'extras', label: 'Extras', value: booking.extrasText || null },
    ]
    if (values.message)
        fields.push({ key: 'notes', label: 'Notes', value: values.message, format: 'multiline' })

    return {
        kind: booking.kind ?? 'tour-booking',
        title: booking.tour,
        contact: { name: values.fullname, email: values.email, phone: values.phone },
        source: { pageUrl: booking.pageUrl },
        fields,
        quote: { displayedTotal: booking.total },
        metadata: {
            ...(booking.itemUuid && { itemUuid: booking.itemUuid }),
            guests: booking.guests,
            ...(booking.guestBreakdown && { guestBreakdown: booking.guestBreakdown }),
        },
        website,
    }
}

export function buildTransferRequest(input: TransferRequestInput): RequestSubmission {
    return {
        kind: 'transfer',
        title: `${input.fromPlaceName} → ${input.toPlaceName}`,
        contact: { name: input.name, email: input.email, phone: input.phone },
        source: { pageUrl: input.pageUrl },
        fields: [
            { key: 'pickup', label: 'Pick-up', value: input.fromPlaceName },
            { key: 'dropoff', label: 'Drop-off', value: input.toPlaceName },
            { key: 'pickupDate', label: 'Date', value: `${input.pickupDate} at ${input.pickupTime}`, format: 'date' },
            ...(input.tripType === 'round_trip'
                ? [{ key: 'returnDate', label: 'Return', value: `${input.returnDate} at ${input.returnTime}`, format: 'date' as const }]
                : []),
            { key: 'passengers', label: 'Passengers', value: input.passengers, format: 'number' },
            { key: 'tripType', label: 'Trip', value: input.tripType === 'round_trip' ? 'Return' : 'One-way' },
        ],
        quote: { displayedTotal: input.displayedTotal },
        metadata: {
            routeId: input.routeId,
            fromPlaceId: input.fromPlaceId,
            toPlaceId: input.toPlaceId,
            tripType: input.tripType,
            pickupDate: input.pickupDate,
            pickupTime: input.pickupTime,
            returnDate: input.returnDate,
            returnTime: input.returnTime,
            passengers: input.passengers,
        },
        website: input.website,
    }
}

export async function sendRequest(
    payload: RequestSubmission,
    fetcher: (url: string, options: { method: 'POST', body: RequestSubmission }) => Promise<unknown> = $fetch,
) {
    await fetcher('/api/requests', { method: 'POST', body: payload })
}
